import config from 'commons-general/config/index.js';
import { isEmpty } from 'lodash';
import { post, get } from '../request';
import { generateHeaders } from './helpers';

import { PAGE_SIZE } from './constants';

const VEHICLES_BASE_API_URL = config.endpoint.vehicles;
const WIDGETS_BASE_API_URL = config.endpoint.widget;

const postRequest = (url, lang, queryParams, body) =>
    post(url, body, {
        headers: generateHeaders(lang),
        params: queryParams,
    });

const getRequest = (url, lang, queryParams) =>
    get(url, {
        headers: generateHeaders(lang, true),
        params: queryParams,
    });

const getNewVehicle = (baseUrl, vehicleId, ...args) =>
    postRequest(...[`${baseUrl}/inventory/vehicle-new/vdp/${vehicleId}`, ...args]);

const getUsedVehicle = (baseUrl, vehicleId, ...args) =>
    postRequest(...[`${baseUrl}/inventory/vehicle-used/vdp/${vehicleId}`, ...args]);

const getUsedVehicles = (baseUrl, ...args) => postRequest(...[`${baseUrl}/inventory/vehicles-used`, ...args]);
const getNewVehicles = (baseUrl, ...args) => postRequest(...[`${baseUrl}/inventory/vehicles-new`, ...args]);

const getFilterMetadatas = (baseUrl, colander, ...args) =>
    getRequest(...[`${baseUrl}/widgets/filters/${colander}-inventory`, ...args]);

// eslint-disable-next-line no-unused-vars
const getCatalogVehicles = (baseUrl, ...args) => get(...[`${baseUrl}/catalog`, ...args]);

const loadFilterMetadatas = (filters, dealerConfig, lang = 'en') => {
    const queryParams = {
        orgId: dealerConfig.orgId,
        orgUnitId: dealerConfig.orgUnitId,
        websiteId: dealerConfig.websiteId,
        soldDaysShown: dealerConfig.soldDaysShown,
        makeId: encodeURI(filters?.makeIds),
        modelId: encodeURI(filters?.modelIds),
        minYear: filters?.yearRange?.from,
        maxYear: filters?.yearRange?.to,
        text: isEmpty(filters?.textQuery) ? undefined : filters.textQuery,
        minOdometer: filters?.odometerRange?.from,
        maxOdometer: filters?.odometerRange?.to,
        minPrice: filters?.salePriceRange?.from,
        maxPrice: filters?.salePriceRange?.to,
        frameStyleId: encodeURI(filters?.frameStyleTypes),
        transmissionId: encodeURI(filters?.transmissionTypes),
        driveTrainId: encodeURI(filters?.drivetrains),
        fuelId: encodeURI(filters?.fuelTypes),
        exteriorColorId: encodeURI(filters?.exteriorColorIds),
        // onlineVehiclesOnly: null,
        // featured: null,
        // years: null,
        // trimIds: null,
        // catalogModelIds: null,
        // engineIds: null,
        // engineDescriptionIds: null,
        // interiorColorIds: null,
        // primaryOrganizationUnitIds: null,
        // vehicleIdsToExclude: null,
        // searchCircle: null,
        // passengers: null,
        // doors: null,
    };

    return getFilterMetadatas(WIDGETS_BASE_API_URL, dealerConfig?.colanderUrl, lang, queryParams);
};

const loadVehicle = (vehicleId, condition, dealerConfig, lang = 'en', paymentOptionsData = {}) => {
    const { cashDown, tradeIn, lien, quoteId } = paymentOptionsData;

    const body = {
        paymentOptionRequest: {
            purchaseMethod: 'cash',
            kmPerYearPlan: 16000,
            paymentFrequency: 52,
            term: 60,
            cashDown: cashDown || 0,
            tradeIn: tradeIn || 0,
            lien: lien || 0,
            saleType: 'retail',
            taxPlan: 'standard',
            priceIncreaseRollCount: 1,
        },
    };

    if (!isEmpty(quoteId)) body.paymentOptionRequest.quoteId = quoteId;

    const queryParams = {
        includeMetadata: true,
        location: dealerConfig.location,
        organizationId: dealerConfig.orgId,
        organizationUnitId: dealerConfig.orgUnitId,
    };

    const getVehicle = condition === 'new' ? getNewVehicle : getUsedVehicle;

    return getVehicle(VEHICLES_BASE_API_URL, vehicleId, lang, queryParams, body);
};

const loadVehicles = async (filters, dealerConfig, isNew, lang = 'en', options = {}, paymentOptionsData = {}) => {
    const {
        preferredPaymentFrequency,
        purchaseMethod,
        initialCashDown,
        maximumTerm,
        tradeIn,
        lien,
    } = paymentOptionsData;

    // Payment options
    const paymentOptions = {
        cashDown: initialCashDown || 0.0,
        financePlan: null,
        kmPerYearPlan: null,
        lien: lien || 0.0,
        paymentFrequency: preferredPaymentFrequency || 52,
        purchaseMethod: purchaseMethod || 'cash',
        saleType: 'retail',
        taxPlan: 'standard',
        term: maximumTerm || 96,
        tradeIn: tradeIn || 0.0,
    };

    const priceIncreaseRollCount = 0;

    // Filtering the vehicle list
    // TO DO: adding most of missing fields
    const filtersV25 = {
        makeIds: filters?.makeIds,
        modelIds: filters?.modelIds,
        colanderSlug: filters?.colanderSlug,
        minYear: filters?.yearRange?.from,
        maxYear: filters?.yearRange?.to,
        soldDaysShown: dealerConfig.soldDaysShown, // Injecting soldDaysShown from props instead of having a parameter in the URL
        textSearch: filters?.textQuery,
        minOdometer: filters?.odometerRange?.from,
        maxOdometer: filters?.odometerRange?.to,
        minSalePrice: filters?.salePriceRange?.from,
        maxSalePrice: filters?.salePriceRange?.to,
        frameStyleIds: filters?.frameStyleTypes,
        transmissionIds: filters?.transmissionTypes,
        driveTrainIds: filters?.drivetrains,
        fuelIds: filters?.fuelTypes,
        exteriorColorIds: filters?.exteriorColorIds,
        vehicleInventoryStatuses: filters?.vehicleStatuses,
        // onlineVehiclesOnly: null,
        // featured: null,
        // years: null,
        // trimIds: null,
        // catalogModelIds: null,
        // engineIds: null,
        // engineDescriptionIds: null,
        // interiorColorIds: null,
        // primaryOrganizationUnitIds: null,
        // vehicleIdsToExclude: null,
        // searchCircle: null,
        // passengers: null,
        // doors: null,
    };

    const { pageNumber = 1, pageSize = PAGE_SIZE, makePriority, sortList } = options;

    const { location, orgId, orgUnitId } = dealerConfig;

    const queryParams = {
        includeMetadata: true,
        location,
        organizationId: orgId,
        organizationUnitId: orgUnitId,
    };

    const getVehicles = isNew ? getNewVehicles : getUsedVehicles;

    const {
        pagination,
        totalElements,
        inventoryVehicles,
        paymentOptionsMetadata,
        tradeInTaxCreditsDetails,
    } = await getVehicles(VEHICLES_BASE_API_URL, lang, queryParams, {
        pagination: {
            pageNumber,
            pageSize,
        },
        paymentOptionRequest: {
            ...paymentOptions,
            priceIncreaseRollCount,
        },
        makePriority: makePriority.map((make) => make.id),
        sortList,
        vehicle: filtersV25,
    });

    return { pagination, totalElements, inventoryVehicles, paymentOptionsMetadata, tradeInTaxCreditsDetails };
};

export * from './constants';
export { loadVehicle, loadVehicles, loadFilterMetadatas };

export default {
    loadVehicle,
    loadVehicles,
    loadFilterMetadatas,
};
